import { useState, useCallback, useMemo } from "react";

import { prepareImageFile } from "../utils/imageUpload";
import useLocalStorage from "../hooks/useLocalStorage";

const useFormData = (initialValues, schema) => {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const { setValue } = useLocalStorage();

  const validateSchema = useMemo(() => {
    return schema ? schema.clone() : null;
  }, [schema]);

  // 画像ファイルを判定・圧縮する（Issue #156）。失敗時は errors にセットして undefined を返す
  const pickImage = useCallback(async (event, fieldName) => {
    const picked = event.target.files[0];
    // 同じファイルを選び直しても change が発火するように input をクリアする
    event.target.value = "";
    const { file, error } = await prepareImageFile(picked, fieldName);
    if (error) {
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: error }));
      return undefined;
    }
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    return file;
  }, []);

  const handleChange = useCallback(
    async (event, index) => {
      const { id, name, value, files } = event.target;
      const fieldName = name || id; // Use id if available, otherwise use name

      let newValue = value;
      let updateFunction = (prevData) => ({
        ...prevData,
        [fieldName]: newValue,
      });

      if (files && files.length > 0) {
        const result = await pickImage(event, fieldName);
        if (!result) return;
        newValue = result;
        updateFunction = (prevData) => ({ ...prevData, [fieldName]: newValue });
      } else if (index !== undefined) {
        newValue = [...formData[fieldName]];
        newValue[index] = value;
        updateFunction = (prevData) => ({ ...prevData, [fieldName]: newValue });
      }

      setFormData(updateFunction);
      setTouched((prevTouched) => ({ ...prevTouched, [fieldName]: true }));
    },
    [formData, pickImage],
  );

  const handleChangeWithStore = useCallback(
    async (event, index) => {
      const { id, name, value, files } = event.target;
      const fieldName = name || id; // Use id if available, otherwise use name

      let newValue = value;
      let updateFunction = (prevData) => {
        setValue([fieldName], newValue);
        return { ...prevData, [fieldName]: newValue };
      };

      if (files && files.length > 0) {
        const result = await pickImage(event, fieldName);
        if (!result) return;
        newValue = result;
        updateFunction = (prevData) => ({ ...prevData, [fieldName]: newValue });
      } else if (index !== undefined) {
        newValue = [...formData[fieldName]];
        newValue[index] = value;
        // updateFunction = (prevData) => ({ ...prevData, [fieldName]: newValue });
      }

      setFormData(updateFunction);
      setTouched((prevTouched) => ({ ...prevTouched, [fieldName]: true }));
    },
    [formData, setValue, pickImage],
  );

  const handleBlur = useCallback((event) => {
    const { name } = event.target;
    setTouched((prevTouched) => ({ ...prevTouched, [name]: true }));
  }, []);

  const validateField = useCallback(
    async (fieldName) => {
      if (!validateSchema) return;

      try {
        await validateSchema.validateAt(fieldName, formData);
        setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
      } catch (error) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: error.message,
        }));
      }
    },
    [formData, validateSchema],
  );

  const validateForm = useCallback(async () => {
    if (!validateSchema) return;

    setIsValidating(true);
    try {
      await validateSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (errors) {
      const newErrors = {};
      errors.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [formData, validateSchema]);

  const resetForm = useCallback(() => {
    setFormData(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  const resetField = useCallback(
    (fieldName) => {
      setFormData((prevData) => ({
        ...prevData,
        [fieldName]: initialValues[fieldName],
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
      setTouched((prevTouched) => ({
        ...prevTouched,
        [fieldName]: false,
      }));
    },
    [initialValues],
  );

  const submitForm = useCallback(
    async (onSubmit) => {
      const isValid = await validateForm();
      console.log(errors);

      if (isValid) {
        onSubmit(formData);
      }
    },
    [formData, validateForm, errors],
  );

  return {
    formData,
    errors,
    touched,
    isValidating,
    resetField,
    handleChangeWithStore,
    handleChange,
    handleBlur,
    validateField,
    validateForm,
    resetForm,
    submitForm,
  };
};

export default useFormData;
